import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const AddGuestModal = ({ isOpen, onClose, onSave, existingGuest = null }) => {
  const [formData, setFormData] = useState({
    name: existingGuest?.name || '',
    email: existingGuest?.email || '',
    phone: existingGuest?.phone || '',
    familyGroup: existingGuest?.familyGroup || '',
    dietaryRestrictions: existingGuest?.dietaryRestrictions || [],
    notes: existingGuest?.notes || '',
    // New gift management fields
    bringsGiftsForKids: existingGuest?.bringsGiftsForKids || false,
    preferredGiftTypes: existingGuest?.preferredGiftTypes || [],
    giftBudgetRange: existingGuest?.giftBudgetRange || '',
    giftNotes: existingGuest?.giftNotes || '',
    // New wine/food preference fields
    winePreferences: existingGuest?.winePreferences || '',
    foodPreferences: existingGuest?.foodPreferences || '',
    postDinnerComments: existingGuest?.postDinnerComments || []
  });

  const [newRestriction, setNewRestriction] = useState({
    name: '',
    type: 'preference',
    severity: ''
  });

  const [errors, setErrors] = useState({});

  const dietaryTypeOptions = [
    { value: 'allergy', label: 'Allergy' },
    { value: 'intolerance', label: 'Intolerance' },
    { value: 'preference', label: 'Preference' }
  ];

  const severityOptions = [
    { value: 'mild', label: 'Mild' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'severe', label: 'Severe' }
  ];

  const giftTypeOptions = [
    { value: 'toys', label: 'Toys & Games' },
    { value: 'books', label: 'Books' },
    { value: 'crafts', label: 'Arts & Crafts' },
    { value: 'educational', label: 'Educational Items' },
    { value: 'clothing', label: 'Clothing' },
    { value: 'outdoor', label: 'Outdoor Activities' },
    { value: 'other', label: 'Other' }
  ];

  const budgetRangeOptions = [
    { value: 'under_25', label: 'Under $25' },
    { value: '25_50', label: '$25 - $50' },
    { value: '50_100', label: '$50 - $100' },
    { value: 'over_100', label: 'Over $100' },
    { value: 'no_preference', label: 'No Preference' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const addDietaryRestriction = () => {
    if (newRestriction?.name?.trim()) {
      setFormData({
        ...formData,
        dietaryRestrictions: [...formData?.dietaryRestrictions, { ...newRestriction }]
      });
      setNewRestriction({ name: '', type: 'preference', severity: '' });
    }
  };

  const removeDietaryRestriction = (index) => {
    setFormData({
      ...formData,
      dietaryRestrictions: formData?.dietaryRestrictions?.filter((_, i) => i !== index)
    });
  };

  const handleGiftTypeChange = (giftType, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        preferredGiftTypes: [...formData?.preferredGiftTypes, giftType]
      });
    } else {
      setFormData({
        ...formData,
        preferredGiftTypes: formData?.preferredGiftTypes?.filter(type => type !== giftType)
      });
    }
  };

  const getDietaryChipColor = (type) => {
    switch (type) {
      case 'allergy':
        return 'bg-error text-error-foreground';
      case 'intolerance':
        return 'bg-warning text-warning-foreground';
      case 'preference':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-warm-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {existingGuest ? 'Edit Guest' : 'Add New Guest'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                required
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                error={errors?.name}
                placeholder="Enter guest's full name"
              />
              
              <Input
                label="Email Address"
                type="email"
                required
                value={formData?.email}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                error={errors?.email}
                placeholder="guest@example.com"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Phone Number"
                type="tel"
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                placeholder="+1 (555) 123-4567"
              />
              
              <Input
                label="Family Group"
                type="text"
                value={formData?.familyGroup}
                onChange={(e) => setFormData({ ...formData, familyGroup: e?.target?.value })}
                placeholder="e.g., Smith Family"
                description="Optional: Group related guests together"
              />
            </div>
          </div>

          {/* Gift Management Section */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Gift" size={20} />
              <span>Gift Management for Kids</span>
            </h3>
            
            <div className="bg-muted/30 p-4 rounded-lg space-y-4">
              <Checkbox
                label="This guest brings gifts for kids"
                checked={formData?.bringsGiftsForKids}
                onChange={(e) => setFormData({ ...formData, bringsGiftsForKids: e?.target?.checked })}
                description="Check if this guest typically brings gifts for children at dinner events"
              />

              {formData?.bringsGiftsForKids && (
                <div className="space-y-4 pl-6 border-l-2 border-primary/20">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Preferred Gift Types (select all that apply)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {giftTypeOptions?.map((option) => (
                        <Checkbox
                          key={option?.value}
                          label={option?.label}
                          checked={formData?.preferredGiftTypes?.includes(option?.value)}
                          onChange={(e) => handleGiftTypeChange(option?.value, e?.target?.checked)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Typical Gift Budget Range"
                      options={budgetRangeOptions}
                      value={formData?.giftBudgetRange}
                      onChange={(value) => setFormData({ ...formData, giftBudgetRange: value })}
                      placeholder="Select budget range"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Gift Notes & Preferences
                    </label>
                    <textarea
                      value={formData?.giftNotes}
                      onChange={(e) => setFormData({ ...formData, giftNotes: e?.target?.value })}
                      placeholder="Any specific preferences, age ranges, or notes about gifts this guest typically brings..."
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Wine & Food Preferences Section */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Wine" size={20} />
              <span>Wine & Food Preferences</span>
            </h3>
            
            <div className="bg-muted/30 p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Wine Preferences
                  </label>
                  <textarea
                    value={formData?.winePreferences}
                    onChange={(e) => setFormData({ ...formData, winePreferences: e?.target?.value })}
                    placeholder="e.g., Prefers red wine, likes Cabernet Sauvignon, doesn't drink alcohol..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Food Preferences & Comments
                  </label>
                  <textarea
                    value={formData?.foodPreferences}
                    onChange={(e) => setFormData({ ...formData, foodPreferences: e?.target?.value })}
                    placeholder="e.g., Loves Italian cuisine, enjoys spicy food, prefers organic ingredients..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dietary Restrictions */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Dietary Restrictions</h3>
            
            {/* Add New Restriction */}
            <div className="bg-muted/30 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Input
                    label="Restriction/Preference"
                    type="text"
                    value={newRestriction?.name}
                    onChange={(e) => setNewRestriction({ ...newRestriction, name: e?.target?.value })}
                    placeholder="e.g., Peanuts, Vegetarian, Gluten"
                  />
                </div>
                
                <Select
                  label="Type"
                  options={dietaryTypeOptions}
                  value={newRestriction?.type}
                  onChange={(value) => setNewRestriction({ ...newRestriction, type: value })}
                />
                
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addDietaryRestriction}
                    disabled={!newRestriction?.name?.trim()}
                    iconName="Plus"
                    iconPosition="left"
                    className="w-full"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {newRestriction?.type === 'allergy' && (
                <div className="mt-3">
                  <Select
                    label="Severity (for allergies)"
                    options={severityOptions}
                    value={newRestriction?.severity}
                    onChange={(value) => setNewRestriction({ ...newRestriction, severity: value })}
                    placeholder="Select severity level"
                  />
                </div>
              )}
            </div>

            {/* Current Restrictions */}
            {formData?.dietaryRestrictions?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Current Restrictions</h4>
                <div className="space-y-2">
                  {formData?.dietaryRestrictions?.map((restriction, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getDietaryChipColor(restriction?.type)}`}>
                          {restriction?.type === 'allergy' && <Icon name="AlertTriangle" size={10} />}
                          {restriction?.type === 'intolerance' && <Icon name="AlertCircle" size={10} />}
                          {restriction?.type === 'preference' && <Icon name="Heart" size={10} />}
                          <span>{restriction?.name}</span>
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          ({restriction?.type})
                        </span>
                        {restriction?.severity && (
                          <span className="text-xs px-2 py-1 bg-muted rounded-full">
                            {restriction?.severity}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDietaryRestriction(index)}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
              placeholder="Any additional information about this guest, their preferences, or special considerations..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
            >
              {existingGuest ? 'Update Guest' : 'Add Guest'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGuestModal;